import React, { useId } from "react";
import { twMerge } from "tailwind-merge";
import { INPUT_BASE, INPUT_PADDING, INPUT_STATE } from "./inputStyles";

interface TextareaProps {
  id?: string; // Element id, e.g. for <Label htmlFor> association
  ariaLabel?: string; // Accessible name when no associated <label> is present
  placeholder?: string; // Placeholder text
  rows?: number; // Number of rows
  value?: string; // Current value
  onChange?: (value: string) => void; // Change handler
  onBlur?: () => void; // Blur handler, e.g. react-hook-form field.onBlur
  className?: string; // Additional CSS classes
  disabled?: boolean; // Disabled state
  error?: boolean; // Error state
  hint?: string; // Hint text to display
  hintId?: string; // Id for the hint element; generated when omitted
  required?: boolean; // Announces the field as required via aria-required
}

const TextArea: React.FC<TextareaProps> = ({
  id,
  ariaLabel,
  placeholder = "Enter your message", // Default placeholder
  rows = 3, // Default number of rows
  value = "", // Default value
  onChange, // Callback for changes
  onBlur,
  className = "", // Additional custom styles
  disabled = false, // Disabled state
  error = false, // Error state
  hint = "", // Default hint text
  hintId,
  required = false,
}) => {
  const generatedId = useId();
  const hintElementId = hintId ?? `${id ?? generatedId}-hint`;
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const stateClasses = disabled
    ? INPUT_STATE.disabled
    : error
      ? INPUT_STATE.error
      : INPUT_STATE.default;
  const textareaClasses = twMerge(INPUT_BASE, INPUT_PADDING, "block h-auto", className, stateClasses);

  return (
    <div className="relative">
      <textarea
        id={id}
        aria-label={ariaLabel}
        placeholder={placeholder}
        rows={rows}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        aria-invalid={error || undefined}
        aria-required={required || undefined}
        aria-describedby={hint ? hintElementId : undefined}
        disabled={disabled}
        className={textareaClasses}
      />
      {hint && (
        <p
          id={hintElementId}
          className={`mt-1.5 text-sm ${
            error ? "text-mp-error" : "text-mp-muted"
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
};

export default TextArea;
